import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import { Helmet } from 'react-helmet-async';
import api from '../utils/api';
import { trackUserRegistration } from '../utils/analytics';
import Footer from '../components/Footer';
import { getStates, getCitiesForState } from '../utils/locationData';

const Signup = () => {
  const [step, setStep] = useState(1); // 1: Email, 2: OTP & Details
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    state: '',
    city: '',
    password: '',
    confirmPassword: ''
  });
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, message: '', color: '' });
  const [emailError, setEmailError] = useState('');
  const [mobileError, setMobileError] = useState('');

  const [resendTimer, setResendTimer] = useState(0);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;

    let processedValue = value;

    // For phone number, only allow digits and limit to 10 characters
    if (name === 'phone') {
      processedValue = value.replace(/\D/g, '').slice(0, 10);
    }

    setFormData({
      ...formData,
      [name]: processedValue
    });

    // Real-time email validation
    if (name === 'email') {
      validateEmail(processedValue);
    }

    // Real-time mobile validation
    if (name === 'phone') {
      validateMobile(processedValue);
    }

    // Clear city when state changes
    if (name === 'state') {
      setFormData({
        ...formData,
        [name]: processedValue,
        city: '' // Clear city when state changes
      });
    } else {
      setFormData({
        ...formData,
        [name]: processedValue
      });
    }

    // Real-time email validation
    if (name === 'email') {
      validateEmail(processedValue);
    }

    // Real-time password strength check
    if (name === 'password') {
      checkPasswordStrength(processedValue);
    }
  };

  // Email validation function
  const validateEmail = (email) => {
    if (!email) {
      setEmailError('');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    // Check for reputable email providers
    const reputableProviders = [
      'gmail.com', 'googlemail.com',
      'yahoo.com', 'yahoo.co.uk', 'yahoo.ca', 'yahoo.in', 'ymail.com',
      'outlook.com', 'outlook.in', 'hotmail.com', 'live.com', 'msn.com',
      'zoho.com', 'zohomail.com',
      'icloud.com', 'me.com', 'mac.com',
      'protonmail.com', 'proton.me',
      'aol.com', 'mail.com', 'gmx.com'
    ];

    const domain = email.toLowerCase().split('@')[1];
    if (!reputableProviders.includes(domain)) {
      setEmailError('Please use a reputable email provider (Gmail, Yahoo, Outlook, Zoho, etc.)');
      return;
    }

    setEmailError('');
  };

  // Mobile validation function
  const validateMobile = (mobile) => {
    if (!mobile) {
      setMobileError('');
      return;
    }

    // Remove any spaces, dashes, or other non-digit characters
    const cleanMobile = mobile.replace(/\D/g, '');

    if (cleanMobile.length === 0) {
      setMobileError('Please enter a valid mobile number');
      return;
    }

    if (cleanMobile.length < 10) {
      setMobileError(`Mobile number must be 10 digits (${cleanMobile.length}/10)`);
      return;
    }

    if (cleanMobile.length > 10) {
      setMobileError('Mobile number cannot be more than 10 digits');
      return;
    }

    // Check if it starts with valid Indian mobile prefixes (6, 7, 8, 9)
    if (!/^[6-9]/.test(cleanMobile)) {
      setMobileError('Mobile number must start with 6, 7, 8, or 9');
      return;
    }

    setMobileError('');
  };

  // Password strength checker
  const checkPasswordStrength = (password) => {
    if (!password) {
      setPasswordStrength({ score: 0, message: '', color: '' });
      return;
    }

    let score = 0;
    let messages = [];

    // Length check
    if (password.length >= 6) {
      score += 1;
    } else {
      messages.push('at least 6 characters');
    }

    // Lowercase check
    if (/[a-z]/.test(password)) {
      score += 1;
    } else {
      messages.push('one lowercase letter');
    }

    // Uppercase check
    if (/[A-Z]/.test(password)) {
      score += 1;
    } else {
      messages.push('one uppercase letter');
    }

    // Special character check
    if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      score += 1;
    } else {
      messages.push('one special character');
    }

    // Set strength message and color
    let message = '';
    let color = '';

    if (score === 0) {
      message = 'Enter a password';
      color = '#6B7280';
    } else if (score === 1) {
      message = `Weak - Missing: ${messages.join(', ')}`;
      color = '#EF4444';
    } else if (score === 2) {
      message = `Fair - Missing: ${messages.join(', ')}`;
      color = '#F59E0B';
    } else if (score === 3) {
      message = `Good - Missing: ${messages.join(', ')}`;
      color = '#10B981';
    } else if (score === 4) {
      message = 'Strong password!';
      color = '#059669';
    }

    setPasswordStrength({ score, message, color });
  };

  // Step 1: Send OTP
  const handleSendOTP = async (e) => {
    e.preventDefault();

    if (!formData.email) {
      toast.error('Please enter your email address');
      return;
    }

    if (emailError) {
      toast.error(emailError);
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/auth/send-otp', {
        email: formData.email
      });

      if (response.data) {
        toast.success('OTP sent to your email address');
        setStep(2);
        startResendTimer();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify OTP and complete signup
  const handleVerifyOTP = async (e) => {
    e.preventDefault();

    if (!otp) {
      toast.error('Please enter the OTP');
      return;
    }

    if (otp.length !== 6) {
      toast.error('OTP must be 6 digits');
      return;
    }

    // Validate all required fields
    if (!formData.name.trim()) {
      toast.error('Please enter your full name');
      return;
    }

    if (!formData.phone.trim()) {
      toast.error('Please enter your phone number');
      return;
    }

    if (mobileError) {
      toast.error(mobileError);
      return;
    }

    if (!formData.state.trim()) {
      toast.error('Please select your state');
      return;
    }

    if (!formData.city.trim()) {
      toast.error('Please select your city');
      return;
    }

    if (!formData.password.trim()) {
      toast.error('Please enter a password');
      return;
    }

    if (!formData.confirmPassword.trim()) {
      toast.error('Please confirm your password');
      return;
    }

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    // Validate password strength
    if (passwordStrength.score < 4) {
      toast.error('Password must contain at least 6 characters, one uppercase, one lowercase, and one special character');
      return;
    }

    setLoading(true);

    try {
      const requestData = {
        email: formData.email,
        otp,
        name: formData.name,
        phone: formData.phone,
        password: formData.password,
        location: `${formData.city}, ${formData.state}` // Combine city and state
      };

      const response = await api.post('/auth/verify-otp', requestData);

      if (response.data) {
        toast.success('Account created successfully!');

        // Track successful registration
        trackUserRegistration('email');

        // Auto login after successful signup
        const loginResult = await login(formData.email, formData.password);
        if (loginResult.success) {
          navigate('/home');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to verify OTP');
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const handleResendOTP = async () => {
    if (resendTimer > 0) return;

    setLoading(true);

    try {
      const response = await api.post('/auth/resend-otp', {
        email: formData.email
      });

      if (response.data) {
        toast.success('OTP resent successfully');
        startResendTimer();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  // Start resend timer
  const startResendTimer = () => {
    setResendTimer(60);
    const timer = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Go back to previous step
  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  return (
    <>
      <Helmet>
        <title>Sign Up - VestrioZ</title>
        <meta
          name="description"
          content="Create your free VestrioZ account and start renting, sharing, and selling fashion items today."
        />
        <meta name="keywords" content="signup, register, VestrioZ, fashion rental, create account" />
      </Helmet>
      <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #DFD0B8 0%, #f5f1e8 100%)' }}>
        {/* Navigation */}
        <nav className="backdrop-blur-md shadow-xl border-b" style={{ background: 'rgba(223, 208, 184, 0.95)', borderColor: 'rgba(148, 137, 121, 0.3)' }}>
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex justify-between items-center py-2">
              <div className="flex items-center">
                <Link to="/" className="flex items-center">
                  <img
                    src="/images/VESTRIOZ.png"
                    alt="VestrioZ Logo"
                    className="h-14 sm:h-16 w-auto object-contain"
                    onError={(e) => {
                      // Fallback to icon if logo image fails to load
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="h-14 sm:h-16 w-14 sm:w-16 rounded-xl flex items-center justify-center shadow-lg" style={{ backgroundColor: '#222831', display: 'none' }}>
                    <i className="fas fa-exchange-alt text-white text-xl sm:text-2xl"></i>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </nav>

        {/* Signup Form */}
        <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full">
            <div className="backdrop-blur-sm rounded-2xl shadow-2xl p-8 border" style={{ background: 'rgba(255, 255, 255, 0.9)', borderColor: 'rgba(148, 137, 121, 0.2)' }}>
              <div className="text-center mb-8">
                <div className="w-16 h-16 gradient-secondary rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <i className="fas fa-user-plus text-white text-2xl"></i>
                </div>
                <h2 className="text-3xl font-bold" style={{ color: '#222831' }}>
                  Join VestrioZ
                </h2>
                <p className="mt-2" style={{ color: '#393E46' }}>Create your account to get started</p>
              </div>

              {/* Step Progress Indicator */}
              <div className="step-indicator">
                <div className="flex items-center space-x-4">
                  <div className={`step-circle ${step >= 1 ? 'step-active' : 'step-inactive'}`}>
                    1
                  </div>
                  <div className={`step-line ${step >= 2 ? 'step-line-active' : 'step-line-inactive'}`}></div>
                  <div className={`step-circle ${step >= 2 ? 'step-active' : 'step-inactive'}`}>
                    2
                  </div>
                </div>
              </div>

              {/* Step 1: Email Address */}
              {step === 1 && (
                <form className="space-y-4" onSubmit={handleSendOTP}>
                  <div className="text-center mb-4">
                    <h3 className="text-lg font-semibold" style={{ color: '#222831' }}>
                      Verify Your Email Address
                    </h3>
                    <p className="text-sm" style={{ color: '#393E46' }}>
                      We'll send you an OTP to verify your email address
                    </p>
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium mb-1" style={{ color: '#393E46' }}>
                      Email Address
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className={`form-input w-full ${emailError ? 'border-red-500' : ''}`}
                      placeholder="Enter your email address"
                    />
                    {emailError && (
                      <p className="text-xs mt-1 text-red-600">
                        <i className="fas fa-exclamation-circle mr-1"></i>
                        {emailError}
                      </p>
                    )}
                    {!emailError && formData.email && (
                      <p className="text-xs mt-1 text-green-600">
                        <i className="fas fa-check-circle mr-1"></i>
                        Valid email address
                      </p>
                    )}
                    <p className="text-xs mt-1" style={{ color: '#948979' }}>
                      We'll send a verification code to this email
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full btn-primary flex items-center justify-center mt-6"
                  >
                    <i className={`fas ${loading ? 'fa-circle-notch fa-spin' : 'fa-envelope'} mr-2`}></i>
                    {loading ? 'Sending OTP...' : 'Send OTP'}
                  </button>

                  <div className="text-center mt-4">
                    <Link to="/login" className="font-medium hover:underline transition-all" style={{ color: '#1D546C' }}>
                      Already have an account? Sign in
                    </Link>
                  </div>
                </form>
              )}

              {/* Step 2: OTP Verification & Account Details */}
              {step === 2 && (
                <form className="space-y-4" onSubmit={handleVerifyOTP}>
                  <div className="text-center mb-4">
                    <h3 className="text-lg font-semibold" style={{ color: '#222831' }}>
                      Enter OTP & Complete Profile
                    </h3>
                    <p className="text-sm" style={{ color: '#393E46' }}>
                      OTP sent to {formData.email}
                    </p>
                  </div>

                  <div>
                    <label htmlFor="otp" className="block text-sm font-medium mb-1" style={{ color: '#393E46' }}>
                      Enter OTP
                    </label>
                    <input
                      id="otp"
                      name="otp"
                      type="text"
                      required
                      maxLength="6"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                      className="form-input w-full otp-input"
                      placeholder="000000"
                    />
                    <div className="flex justify-between items-center mt-2">
                      <button
                        type="button"
                        onClick={handleBack}
                        className="text-sm hover:underline"
                        style={{ color: '#393E46' }}
                      >
                        <i className="fas fa-arrow-left mr-1"></i>
                        Change Email
                      </button>
                      <button
                        type="button"
                        onClick={handleResendOTP}
                        disabled={resendTimer > 0 || loading}
                        className="text-sm font-medium hover:underline disabled:opacity-50"
                        style={{ color: resendTimer > 0 ? '#948979' : '#948979' }}
                      >
                        {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP'}
                      </button>
                    </div>
                  </div>

                  <div className="border-t pt-4" style={{ borderColor: 'rgba(148, 137, 121, 0.3)' }}>
                    <h4 className="font-medium mb-3" style={{ color: '#393E46' }}>Complete Your Profile</h4>

                    <div className="space-y-3">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium mb-1" style={{ color: '#393E46' }}>
                          Full Name
                        </label>
                        <input
                          id="name"
                          name="name"
                          type="text"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          className="form-input w-full"
                          placeholder="Enter your full name"
                        />
                      </div>

                      <div>
                        <label htmlFor="email" className="block text-sm font-medium mb-1" style={{ color: '#393E46' }}>
                          Email Address
                        </label>
                        <input
                          id="email"
                          name="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          className="form-input w-full"
                          placeholder="Enter your email"
                          readOnly
                        />
                      </div>

                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium mb-1" style={{ color: '#393E46' }}>
                          Phone Number
                        </label>
                        <input
                          id="phone"
                          name="phone"
                          type="tel"
                          required
                          value={formData.phone}
                          onChange={handleChange}
                          className={`form-input w-full ${mobileError ? 'border-red-500' : ''}`}
                          placeholder="Enter your 10-digit mobile number"
                          maxLength="10"
                        />
                        {mobileError && (
                          <p className="text-xs mt-1 text-red-600">
                            <i className="fas fa-exclamation-circle mr-1"></i>
                            {mobileError}
                          </p>
                        )}
                        {!mobileError && formData.phone && formData.phone.replace(/\D/g, '').length === 10 && (
                          <p className="text-xs mt-1 text-green-600">
                            <i className="fas fa-check-circle mr-1"></i>
                            Valid mobile number
                          </p>
                        )}
                        <p className="text-xs mt-1 text-gray-500">
                          Enter 10-digit mobile number (starting with 6, 7, 8, or 9)
                        </p>
                      </div>

                      <div>
                        <label htmlFor="state" className="block text-sm font-medium mb-1" style={{ color: '#393E46' }}>
                          State
                        </label>
                        <select
                          id="state"
                          name="state"
                          required
                          value={formData.state}
                          onChange={handleChange}
                          className="form-input w-full"
                        >
                          <option value="">Select State</option>
                          {getStates().map(state => (
                            <option key={state} value={state}>{state}</option>
                          ))}
                        </select>
                      </div>

                      {formData.state && (
                        <div>
                          <label htmlFor="city" className="block text-sm font-medium mb-1" style={{ color: '#393E46' }}>
                            City
                          </label>
                          <select
                            id="city"
                            name="city"
                            required
                            value={formData.city}
                            onChange={handleChange}
                            className="form-input w-full"
                          >
                            <option value="">Select City</option>
                            {getCitiesForState(formData.state).map(city => (
                              <option key={city} value={city}>{city}</option>
                            ))}
                          </select>
                        </div>
                      )}

                      <div>
                        <label htmlFor="password" className="block text-sm font-medium mb-1" style={{ color: '#393E46' }}>
                          Password
                        </label>
                        <div className="relative">
                          <input
                            id="password"
                            name="password"
                            type={showPassword ? "text" : "password"}
                            required
                            value={formData.password}
                            onChange={handleChange}
                            className="form-input w-full pr-12"
                            placeholder="Create a password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700 transition-colors"
                          >
                            <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                          </button>
                        </div>
                        {formData.password && (
                          <div className="mt-2">
                            <div className="flex items-center space-x-2">
                              <div className="flex-1 bg-gray-200 rounded-full h-2">
                                <div
                                  className="h-2 rounded-full transition-all duration-300"
                                  style={{
                                    width: `${(passwordStrength.score / 4) * 100}%`,
                                    backgroundColor: passwordStrength.color
                                  }}
                                ></div>
                              </div>
                              <span className="text-xs font-medium" style={{ color: passwordStrength.color }}>
                                {passwordStrength.score}/4
                              </span>
                            </div>
                            <p className="text-xs mt-1" style={{ color: passwordStrength.color }}>
                              {passwordStrength.message}
                            </p>
                          </div>
                        )}
                        <p className="text-xs mt-1 text-gray-500">
                          Must contain: 6+ characters, uppercase, lowercase, special character
                        </p>
                      </div>

                      <div>
                        <label htmlFor="confirmPassword" className="block text-sm font-medium mb-1" style={{ color: '#393E46' }}>
                          Confirm Password
                        </label>
                        <div className="relative">
                          <input
                            id="confirmPassword"
                            name="confirmPassword"
                            type={showConfirmPassword ? "text" : "password"}
                            required
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            className="form-input w-full pr-12"
                            placeholder="Confirm your password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700 transition-colors"
                          >
                            <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Legal Agreement */}
                  <div className="text-center mt-4 mb-4">
                    <p className="text-xs text-gray-600 leading-relaxed">
                      By creating an account, you agree to our{' '}
                      <Link
                        to="/privacy-policy"
                        className="text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                      >
                        Privacy Policy
                      </Link>
                      {' '}and{' '}
                      <Link
                        to="/terms-of-service"
                        className="text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                      >
                        Terms of Service
                      </Link>
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={loading || otp.length !== 6}
                    className="w-full btn-primary flex items-center justify-center"
                  >
                    <i className={`fas ${loading ? 'fa-circle-notch fa-spin' : 'fa-user-plus'} mr-2`}></i>
                    {loading ? 'Creating Account...' : 'Verify & Create Account'}
                  </button>

                  <div className="text-center mt-4">
                    <Link to="/login" className="font-medium hover:underline transition-all" style={{ color: '#948979' }}>
                      Already have an account? Sign in
                    </Link>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
};

export default Signup;